import { css } from 'aphrodite';
import { format, isSameDay, isSameMonth } from 'date-fns';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import { ClockAlertIcon } from '../../../components/svgs/icons/ClockAlertIcon';
import { styleSheet } from '../styles';
import { CalendarItem } from '../types';
import { getDayKey } from '../utils/dateRange';
import { formatTime } from '../utils/format';
import { groupItemsByDay } from '../utils/layout';

/** Max item chips rendered per day cell before collapsing into a "+N more". */
const MAX_CHIPS_PER_DAY = 3;

interface IProps {
	focusedDate: Date;
	/** The full month grid (leading/trailing days included); length is a multiple of 7. */
	days: Date[];
	items: CalendarItem[];
	onSelectItem: (item: CalendarItem) => void;
	/** Open a specific day (switches to the day view). */
	onSelectDate: (date: Date) => void;
	/**
	 * More undated action items exist on the server than were fetched onto today. Renders today's
	 * overflow as "N+ more" (rather than an exact "N more") so the roll-up doesn't imply it holds
	 * every undated item.
	 */
	undatedHasMore?: boolean;
}

/** Full-month grid where each day cell stacks its items, with a "+N more" overflow. */
export const MonthView: React.FC<IProps> = ({
	focusedDate,
	days,
	items,
	onSelectItem,
	onSelectDate,
	undatedHasMore = false,
}) => {
	const itemsByDay = React.useMemo(() => groupItemsByDay(items), [items]);
	const weeks = React.useMemo(() => {
		const result: Date[][] = [];
		for (let i = 0; i < days.length; i += 7) {
			result.push(days.slice(i, i + 7));
		}
		return result;
	}, [days]);

	// Advance on a minute cadence so the "today" highlight follows a midnight rollover.
	const [now, setNow] = React.useState(() => new Date());
	React.useEffect(() => {
		const id = setInterval(() => setNow(new Date()), 60_000);
		return () => clearInterval(id);
	}, []);
	const weekdayLabels = weeks[0] ?? days.slice(0, 7);

	return (
		<div className={css(styleSheet.monthGrid)}>
			<div className={css(styleSheet.monthWeekdays)}>
				{weekdayLabels.map(day => (
					<div key={getDayKey(day)} className={css(styleSheet.monthWeekday)}>
						{format(day, 'EEE')}
					</div>
				))}
			</div>

			<div className={css(styleSheet.monthBody)}>
				{weeks.map(week => (
					<div key={getDayKey(week[0])} className={css(styleSheet.monthWeekRow)}>
						{week.map(day => {
							// Undated items sort after dated ones so genuine due work fills the visible
							// slots first and undated overflow collapses into "+N more".
							const dayItems = [...(itemsByDay.get(getDayKey(day)) ?? [])].sort(
								(a, b) => Number(!!a.undated) - Number(!!b.undated) || a.start.getTime() - b.start.getTime()
							);
							const outside = !isSameMonth(day, focusedDate);
							const today = isSameDay(day, now);
							const visible = dayItems.slice(0, MAX_CHIPS_PER_DAY);
							const overflow = dayItems.length - visible.length;

							return (
								<div
									key={getDayKey(day)}
									className={css(styleSheet.monthDayCell, outside && styleSheet.monthDayCellOutside)}
								>
									<Button
										variant='ghost'
										styles={[
											styleSheet.resetButton,
											styleSheet.monthDayNumber,
											outside && styleSheet.monthDayNumberOutside,
											today && styleSheet.monthDayNumberToday,
										]}
										onClick={() => onSelectDate(day)}
										title='View day'
									>
										{format(day, 'd')}
									</Button>

									<div className={css(styleSheet.monthChips)}>
										{visible.map(item => (
											<Button
												key={`${item.kind}-${item.id}`}
												variant='ghost'
												styles={[styleSheet.resetButton, styleSheet.monthChip, item.faded && styleSheet.faded]}
												title={item.title}
												onClick={() => onSelectItem(item)}
												style={{
													borderLeftColor: item.color,
													backgroundColor: `color-mix(in srgb, ${item.color} 14%, var(--color-background-light))`,
												}}
											>
												{item.overdue ? (
													<span className={css(styleSheet.overdueIcon)} title='Overdue'>
														<ClockAlertIcon />
													</span>
												) : item.allDay ? null : (
													<span className={css(styleSheet.meetingBlockTimeInline)}>{formatTime(item.start)}</span>
												)}
												<span
													className={css(styleSheet.meetingBlockTitleInline, item.muted && styleSheet.cancelledText)}
												>
													{item.title}
												</span>
											</Button>
										))}
										{overflow > 0 ? (
											<Button
												variant='ghost'
												styles={[styleSheet.resetButton, styleSheet.monthMore]}
												onClick={() => onSelectDate(day)}
											>
												+{overflow}
												{today && undatedHasMore ? '+' : ''} more
											</Button>
										) : null}
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};
